<template>
  <div class="panorama-page p-2 p-md-3 dark-theme">
    <div class="d-flex align-items-center justify-content-between mb-2 flex-wrap">
      <div class="d-flex align-items-center flex-wrap mb-2 mb-md-0 panorama-toolbar">
        <h3 class="text-white mb-0 font-weight-bold tracking-wide mr-3">
          <b-icon icon="display" class="mr-2 text-warning"></b-icon>策略全景解析
        </h3>
        <div class="d-flex align-items-center bg-dark-pill px-2 py-1 rounded-pill border border-secondary-40">
          <b-icon icon="calendar3" class="text-warning mx-2" font-scale="0.9"></b-icon>
          <b-form-input 
            type="date" 
            v-model="selectedDate" 
            size="sm" 
            class="custom-dark-datepicker border-0 bg-transparent text-white shadow-none px-1" 
            style="width: 135px; font-size: 0.875rem;"
            @change="initializePage"
          ></b-form-input>
          <b-button 
            variant="link" 
            size="sm" 
            class="text-warning p-0 mx-2 text-decoration-none xx-small" 
            @click="resetToToday"
            v-if="selectedDate !== todayStr"
          >
            今天
          </b-button>
        </div>
        <div class="d-flex align-items-center bg-dark-pill px-3 py-1 rounded-pill border border-secondary-40 ml-2">
          <b-form-checkbox
            v-model="supplementH67"
            @change="updateMonitorConfig()"
            switch
            class="text-white custom-dark-switch xx-small"
          >
            <span style="font-size: 0.825rem; font-weight: 500; cursor: pointer;">补充H6/7</span>
          </b-form-checkbox>
          <b-form-checkbox
            v-model="filterTopicNet5"
            @change="updateMonitorConfig()"
            switch
            class="text-white custom-dark-switch xx-small ml-3"
          >
            <span style="font-size: 0.825rem; font-weight: 500; cursor: pointer;">NET5≥0</span>
          </b-form-checkbox>
        </div>
        <b-button
          variant="outline-info"
          size="sm"
          class="rounded-pill px-3 ml-2"
          :disabled="refreshing"
          @click="refreshPage"
        >
          <b-spinner v-if="refreshing" small class="mr-1"></b-spinner>
          <b-icon v-else icon="arrow-clockwise" class="mr-1"></b-icon>刷新
        </b-button>
      </div>
      <b-button variant="outline-light" class="rounded-pill px-4 shadow-sm" @click="$router.back()">
        <b-icon icon="arrow-left" class="mr-1"></b-icon> 返回
      </b-button>
    </div>

    <div class="row mb-2 compact-control-panel">
      <div class="col-12 col-xl-7 mb-2 mb-xl-0">
        <b-card class="bg-card-dark border-light-10 shadow-sm rounded-lg" body-class="p-2">
          <div class="d-flex justify-content-between align-items-center flex-wrap mb-1">
            <div><span class="text-white font-weight-bold">实盘仓位配置</span><span class="text-muted xx-small ml-2">按买入顺序，保存后下一周期生效</span></div>
            <b-button size="sm" variant="warning" class="rounded-pill px-3" :disabled="savingConfig" @click="savePositionConfig"><b-spinner v-if="savingConfig" small class="mr-1"></b-spinner>保存仓位</b-button>
          </div>
          <div class="position-config-row mb-1">
            <div class="strategy-label text-warning">V2</div>
            <div v-for="(value, index) in v2PositionPcts" :key="`v2-${index}`" class="position-input-wrap"><label>第{{ index + 1 }}笔</label><b-input-group size="sm" append="%"><b-form-input v-model.number="v2PositionPcts[index]" type="number" min="0.1" max="100" step="0.5"></b-form-input></b-input-group></div>
            <div class="position-total">
              计划总仓位 <strong>{{ positionTotal(v2PositionPcts) }}%</strong>
              <span class="today-buy-targets ml-2">｜ 当日买入：<strong>{{ todayBuyText('v2') }}</strong></span>
            </div>
          </div>
          <div class="position-config-row">
            <div class="strategy-label text-info">V3</div>
            <div v-for="(value, index) in v3PositionPcts" :key="`v3-${index}`" class="position-input-wrap"><label>第{{ index + 1 }}笔</label><b-input-group size="sm" append="%"><b-form-input v-model.number="v3PositionPcts[index]" type="number" min="0.1" max="100" step="0.5"></b-form-input></b-input-group></div>
            <div class="position-total">
              计划总仓位 <strong>{{ positionTotal(v3PositionPcts) }}%</strong>
              <span class="today-buy-targets ml-2">｜ 当日买入：<strong>{{ todayBuyText('v3') }}</strong></span>
            </div>
          </div>
        </b-card>
      </div>
      <div class="col-12 col-xl-5">
        <b-card class="crowding-card border-light-10 shadow-sm rounded-lg h-100" :class="`crowding-${crowding.level || 'unknown'}`" body-class="p-2">
          <div class="d-flex justify-content-between align-items-start">
            <div><div class="text-white font-weight-bold">题材拥挤度</div><div class="xx-small text-muted mt-1" v-if="crowding.date">指标日期 {{ crowding.date }}<span v-if="crowding.is_stale" class="text-warning ml-1">（非当日数据）</span></div></div>
            <b-badge :variant="crowdingBadgeVariant" class="px-2 py-1">{{ crowdingLevelText }}</b-badge>
          </div>
          <div v-if="crowding.available" class="mt-1 d-flex align-items-center crowding-content">
            <div class="crowding-metric mr-3">
              <div class="d-flex align-items-center flex-wrap">
                <div class="crowding-ratio mr-2">{{ (crowding.positive_ratio * 100).toFixed(1) }}%</div>
                <div class="crowding-threshold-note">&lt;78% 正常 ｜ 78%–&lt;82% 半仓 ｜ ≥82% 禁止新开仓</div>
              </div>
              <div class="text-muted xx-small text-nowrap">
                正预期 {{ crowding.positive_count }}/{{ crowding.valid_count }}，覆盖率 {{ (crowding.coverage_ratio * 100).toFixed(1) }}%（{{ crowding.valid_count }}/{{ crowding.topic_count }}）
                <b-icon icon="question-circle" class="ml-1" title="覆盖率 = 具备有效历史统计的题材数 ÷ 当日题材总数；至少80%才启用拥挤判断"></b-icon>
              </div>
            </div>
            <div class="crowding-result">
              <div class="crowding-advice">{{ crowding.message }}</div>
              <div class="xx-small mt-1 text-light" v-if="crowding.recommendations">V2 {{ recommendedPositionText('v2') }}；V3 {{ recommendedPositionText('v3') }}</div>
            </div>
          </div>
          <div v-else class="text-muted mt-3">{{ crowding.message || '拥挤度数据不可用' }}</div>
        </b-card>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="warning" label="Loading..."></b-spinner>
      <div class="text-muted-gold mt-3">正在获取 {{ selectedDate }} 监控数据...</div>
    </div>

    <div v-else-if="topics.length === 0" class="text-center py-5">
      <b-icon icon="inbox" font-scale="3" class="text-muted mb-3"></b-icon>
      <div class="text-muted">{{ selectedDate }} 无监控题材或暂未获取到数据</div>
    </div>

    <div v-else class="row no-gutters custom-row-5">
      <div v-for="(topic, index) in topics" :key="index" class="col-12 col-md-4 col-xl-20 mb-2 px-1">
        <b-card class="bg-card-dark border-light-10 shadow-sm h-100 rounded-lg overflow-hidden" body-class="p-1 p-md-2">
          <!-- 头部 -->
          <div class="d-flex justify-content-between align-items-start mb-2 pb-2 border-bottom border-light-10">
            <div>
              <h6 class="mb-1 text-white font-weight-bold cursor-pointer hover-primary d-flex align-items-center flex-wrap" :title="topic.topic_name" @click="showTopicKline(topic)">
                <span class="topic-sequence mr-1">{{ index + 1 }}.</span><span>{{ topic.topic_name }}</span>
                <span v-if="topic.h_marker || topic.d20_marker" class="text-muted-gold font-weight-normal xx-small ml-1" style="font-size: 0.75rem;">
                  [{{ [topic.h_marker, topic.d20_marker].filter(Boolean).join(',') }}]
                </span>
              </h6>
              <div class="d-flex align-items-center flex-wrap mb-1">
                <b-badge variant="soft-warning" class="xx-small mr-1 d-inline-block">共 {{ topic.total_candidates }} 备选</b-badge>
                <b-badge 
                  v-if="formatTopicNet5(topic)" 
                  :class="getNet5BadgeClass(topic)" 
                  class="xx-small font-weight-bold d-inline-block"
                  :title="`近5日净涨停: ${formatTopicNet5(topic)}`"
                >
                  NET5 {{ formatTopicNet5(topic) }}
                </b-badge>
              </div>
              <div class="text-muted xx-small" v-if="topic.stats">
                涨跌: <span class="text-danger">{{ topic.stats.up_count }}</span>/<span class="text-success">{{ topic.stats.down_count }}</span>
                <span class="mx-1">|</span>
                均: <span :class="getPnlClass(topic.stats.avg_pct)">{{ topic.stats.avg_pct.toFixed(2) }}%</span>
              </div>
              <div class="text-muted xx-small mt-1" style="max-height: 2.8em; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; line-height: 1.4;" v-if="topic.stats" :title="topic.stats.main_leader + ' (主) / ' + topic.stats.gem_leader + ' (创)'">
                领涨: <span class="text-white">{{ topic.stats.main_leader }} / {{ topic.stats.gem_leader }}</span>
              </div>
              <div class="text-muted xx-small mt-1" style="max-height: 2.8em; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; line-height: 1.4;" :title="topic.stats.limit_ups" v-if="topic.stats">
                涨停: <span class="text-danger">{{ topic.stats.limit_ups }}</span>
              </div>
            </div>
            <div class="text-right">
              <div class="text-muted xx-small">触发/限额</div>
              <div class="text-white xx-small mt-1">
                <span class="text-danger font-weight-bold">{{ topic.alerts.length }}</span>次
                <span class="mx-1">|</span>
                主<span :class="{'text-warning': topic.counts['主板'] >= 2}">{{ topic.counts['主板'] || 0 }}/2</span>
                <span class="mx-1">|</span>
                创<span :class="{'text-warning': topic.counts['双创'] >= 1}">{{ topic.counts['双创'] || 0 }}/1</span>
              </div>
            </div>
          </div>
          
          <!-- 备选股列表 -->
          <div class="table-responsive">
            <table class="table table-dark table-hover table-borderless table-sm mb-0 compact-table xx-small">
              <thead>
                <tr class="text-muted border-bottom border-light-10">
                  <th style="width: 25%">股票</th>
                  <th class="text-right" style="width: 13%">现价</th>
                  <th class="text-right text-nowrap" style="width: 16%">额/值</th>
                  <th class="text-right text-nowrap" style="width: 15%">涨幅</th>
                  <th class="text-center text-nowrap" style="width: 31%">状态</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="stock in getDisplayCandidates(topic.candidates, topic.topic_name)" :key="stock.code6">
                  <td class="align-middle text-nowrap">
                    <a href="javascript:void(0)" @click="showStockInfo(stock.code6, stock.name)" class="text-white text-decoration-none hover-primary font-weight-bold text-truncate d-inline-block align-middle" style="max-width: 58px;" :title="stock.name + ' (' + stock.code6 + ')'">
                      {{ stock.name }}
                    </a>
                    <span
                      v-if="stock.score !== undefined && stock.score !== null"
                      class="score-pill ml-1 align-middle"
                      :class="getScoreClass(stock.score)"
                      :title="getScoreTooltip(stock)"
                    >
                      {{ stock.score > 0 ? '+' : '' }}{{ stock.score }}
                    </span>
                    <span class="text-muted ml-1 align-middle font-monospace" style="font-size: 0.6rem; letter-spacing: -0.2px;">{{ stock.code6 }}</span>
                  </td>
                  <td class="text-right align-middle font-weight-bold font-monospace" :class="getPnlClass(stock.pct_change)">
                    {{ stock.price.toFixed(2) }}
                  </td>
                  <td class="text-right align-middle text-muted text-nowrap font-monospace" style="font-size: 0.62rem;">
                    {{ formatAmountMkv(stock.amount, stock.mkv) }}
                  </td>
                  <td class="text-right align-middle font-weight-bold text-nowrap font-monospace" :class="getPnlClass(stock.pct_change)">
                    {{ stock.pct_change > 0 ? '+' : '' }}{{ stock.pct_change.toFixed(2) }}%
                  </td>
                  <td class="text-center align-middle py-0 px-1">
                    <div class="d-flex align-items-center justify-content-center text-nowrap" :title="getStatusTooltip(stock)">
                      <!-- 1. 智能合并单标签 (当 V2 与 V3 状态一致时，只展示一个精致单标签，消除80%冗余噪音) -->
                      <template v-if="isStatusMerged(stock)">
                        <b-badge :class="getMergedBadgeClass(stock)" class="status-badge-compact text-nowrap">
                          {{ getMergedBadgeText(stock) }}
                        </b-badge>
                      </template>

                      <!-- 2. 状态分化展示 (单行紧凑双胶囊，左右并列) -->
                      <template v-else>
                        <span class="micro-badge" :class="getV2BadgeClass(stock)">
                          <span class="micro-prefix v2-p">2:</span>{{ getV2BadgeText(stock) }}
                        </span>
                        <span class="micro-badge ml-1" :class="getV3BadgeClass(stock)">
                          <span class="micro-prefix v3-p">3:</span>{{ getV3BadgeText(stock) }}
                        </span>
                      </template>

                      <!-- 手动点火微型按钮 (仅今天且V2未买入/未触发时显示) -->
                      <b-button 
                        v-if="selectedDate === todayStr && isCanManualTrigger(stock)"
                        variant="link" 
                        size="sm" 
                        class="p-0 ml-1 fire-btn" 
                        title="手动点火买入"
                        @click.stop="confirmManualTrigger(stock.code6, stock.name)"
                      >
                        🔥
                      </b-button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="topic.candidates.length > 10" class="text-center mt-1">
              <b-button variant="link" size="sm" class="text-muted xx-small p-0" @click="toggleExpand(topic.topic_name)">
                {{ expandedTopics[topic.topic_name] ? '收起' : `查看更多 (${topic.candidates.length - 10})` }}
                <b-icon :icon="expandedTopics[topic.topic_name] ? 'chevron-up' : 'chevron-down'"></b-icon>
              </b-button>
            </div>
          </div>
        </b-card>
      </div>
    </div>
    
    <!-- 引入个股侧边栏 -->
    <StockInfoSidebar 
      :symbol_code="sidebarSymbolCode" 
      :symbol_name="sidebarSymbolName" 
    />

    <!-- 题材K线图模态框 -->
    <TopicKlineModal
      :visible="showTopicKlineModal"
      :uniqueKey="selectedTopicUniqueKey"
      :topicName="selectedTopicName"
      @close="showTopicKlineModal = false"
    />
  </div>
</template>

<script>
import { request_zzquant } from 'network/request.js'
import StockInfoSidebar from '@/components/stock/StockInfoSidebar'
import TopicKlineModal from '@/components/plate/TopicKlineModal.vue'

export default {
  name: 'WonderStrategyPanorama',
  components: {
    StockInfoSidebar,
    TopicKlineModal
  },
  data() {
    const d = new Date()
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    const todayStr = `${year}-${month}-${day}`
    return {
      loading: true,
      topics: [],
      expandedTopics: {},
      sidebarSymbolCode: '',
      sidebarSymbolName: '',
      showTopicKlineModal: false,
      selectedTopicUniqueKey: '',
      selectedTopicName: '',
      selectedDate: todayStr,
      supplementH67: false,
      filterTopicNet5: true,
      v2PositionPcts: [25, 25],
      v3PositionPcts: [15, 15, 8, 7, 5],
      crowding: { available: false, level: 'unknown' },
      todayBuys: { v2: [], v3: [] },
      savingConfig: false,
      refreshing: false
    }
  },
  computed: {
    crowdingBadgeVariant() {
      return { blocked: 'danger', reduced: 'warning', normal: 'success' }[this.crowding.level] || 'secondary'
    },
    crowdingLevelText() {
      return { blocked: '禁止开仓', reduced: '建议半仓', normal: '正常' }[this.crowding.level] || '暂无数据'
    },
    todayStr() {
      const d = new Date()
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
  },
  created() {
    this.initializePage()
  },
  methods: {
    async refreshPage() {
      this.refreshing = true
      try {
        await this.initializePage()
      } finally {
        this.refreshing = false
      }
    },
    async initializePage() {
      try {
        const res = await request_zzquant({
          url: '/v3/topic/monitor/panorama/config',
          method: 'get',
          params: { date: this.selectedDate }
        })
        if (res.data && (res.data.code === 200 || res.data.code === 20000)) {
          const config = res.data.data || {}
          this.supplementH67 = Boolean(config.supplement_h67)
          this.filterTopicNet5 = config.filter_topic_net5 !== false
          this.v2PositionPcts = (config.v2_position_fractions || [0.25, 0.25]).map(value => value * 100)
          this.v3PositionPcts = (config.v3_position_fractions || [0.15, 0.15, 0.08, 0.07, 0.05]).map(value => value * 100)
          this.crowding = config.crowding || { available: false, level: 'unknown' }
        }
      } catch (e) {
        console.error('读取全景配置失败，使用默认值', e)
      }
      await this.fetchTodayBuys()
      await this.fetchData()
    },
    async fetchTodayBuys() {
      try {
        const requests = [
          request_zzquant({ url: '/v3/quant/strategy/daily-details', method: 'get', params: { date: this.selectedDate, account_id: 3 }, noRedirect: true }),
          request_zzquant({ url: '/v3/quant/strategy/daily-details', method: 'get', params: { date: this.selectedDate, account_id: 4 }, noRedirect: true })
        ]
        const [v2Res, v3Res] = await Promise.all(requests)
        this.todayBuys = {
          v2: this.extractBuyTargets(v2Res),
          v3: this.extractBuyTargets(v3Res)
        }
      } catch (e) {
        console.error('读取当日买入标的失败', e)
        this.todayBuys = { v2: [], v3: [] }
      }
    },
    extractBuyTargets(response) {
      const records = response?.data?.data?.records || []
      const seen = new Set()
      return records
        .filter(record => record.trade_type === '买入')
        .filter(record => {
          const key = record.symbol_code || record.symbol_name
          if (!key || seen.has(key)) return false
          seen.add(key)
          return true
        })
        .map(record => ({ code: record.symbol_code, name: record.symbol_name || record.symbol_code }))
    },
    todayBuyText(version) {
      const targets = this.todayBuys[version] || []
      return targets.length ? targets.map(item => item.name).join('、') : '暂无'
    },
    async updateMonitorConfig(refreshAfter = true) {
      if (this.selectedDate !== this.todayStr) {
        await this.fetchData()
        return
      }
      this.loading = true
      try {
        const res = await request_zzquant({
          url: '/v3/topic/monitor/panorama/config',
          method: 'post',
          data: {
            supplement_h67: this.supplementH67,
            filter_topic_net5: this.filterTopicNet5,
            v2_position_fractions: this.v2PositionPcts.map(value => value / 100),
            v3_position_fractions: this.v3PositionPcts.map(value => value / 100)
          }
        })
        if (!res.data || (res.data.code !== 200 && res.data.code !== 20000)) {
          throw new Error(res.data?.msg || res.data?.message || '保存配置失败')
        }
        this.crowding = res.data.data?.crowding || this.crowding
        this.$bvToast.toast('配置已保存，实盘将在下一监控周期刷新候选池', {
          title: '实盘配置',
          variant: 'success',
          solid: true
        })
      } catch (e) {
        console.error(e)
        this.$bvToast.toast(e.response?.data?.detail || e.message || '保存配置失败', {
          title: '错误',
          variant: 'danger',
          solid: true
        })
        await this.initializePage()
        return
      }
      if (refreshAfter) await this.fetchData()
    },
    positionTotal(values) {
      return values.reduce((total, value) => total + (Number(value) || 0), 0).toFixed(1)
    },
    recommendedPositionText(version) {
      const recommendation = this.crowding.recommendations?.[version]
      if (!recommendation) return '暂无'
      if (recommendation.total_position === 0) return '0%（禁止新开仓）'
      const details = recommendation.position_fractions.map(value => `${(value * 100).toFixed(1)}%`).join('/')
      return `总仓位 ${(recommendation.total_position * 100).toFixed(1)}%（${details}）`
    },
    async savePositionConfig() {
      const allValues = [...this.v2PositionPcts, ...this.v3PositionPcts]
      const invalid = allValues.some(value => !Number.isFinite(Number(value)) || Number(value) <= 0 || Number(value) > 100)
      if (invalid || Number(this.positionTotal(this.v2PositionPcts)) > 100 || Number(this.positionTotal(this.v3PositionPcts)) > 100) {
        this.$bvToast.toast('每笔仓位需大于0且不超过100%，单策略总仓位不能超过100%', { title: '仓位配置有误', variant: 'danger', solid: true })
        return
      }
      this.savingConfig = true
      try {
        await this.updateMonitorConfig(false)
      } finally {
        this.savingConfig = false
      }
    },
    toggleExpand(topicName) {
      this.$set(this.expandedTopics, topicName, !this.expandedTopics[topicName])
    },
    getDisplayCandidates(candidates, topicName) {
      if (!candidates) return []
      const isExpanded = this.expandedTopics[topicName]
      if (isExpanded || candidates.length <= 10) {
        return candidates
      }
      return candidates.slice(0, 10)
    },
    showStockInfo(code, name) {
      this.sidebarSymbolCode = code || ''
      this.sidebarSymbolName = name || ''
      this.$nextTick(() => {
        this.$root.$emit('bv::toggle::collapse', 'sidebar-right-stock')
      })
    },
    resetToToday() {
      this.selectedDate = this.todayStr
      this.initializePage()
    },
    async fetchData() {
      this.loading = true
      try {
        const params = {
          supplement_h67: this.supplementH67,
          filter_topic_net5: this.filterTopicNet5
        }
        if (this.selectedDate) {
          params.date = this.selectedDate
        }
        const res = await request_zzquant({
          url: '/v3/topic/monitor/panorama',
          method: 'get',
          params
        })
        if (res.data && (res.data.code === 200 || res.data.code === 20000)) {
          this.topics = res.data.data
        } else {
          this.$bvToast.toast(res.data?.msg || res.data?.message || '获取数据失败', {
            title: '错误',
            variant: 'danger',
            solid: true
          })
        }
      } catch (e) {
        console.error(e)
        this.$bvToast.toast('网络请求异常', {
          title: '错误',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.loading = false
      }
    },
    getPnlClass(val) {
      if (val > 0) return 'text-danger'
      if (val < 0) return 'text-success'
      return 'text-muted'
    },
    formatTopicNet5(topic) {
      if (!topic) return ''
      const net = topic.net5 !== undefined && topic.net5 !== null ? topic.net5 : topic.NET5
      if (net === undefined || net === null) return ''
      const sign = net > 0 ? '+' : ''
      if (topic.limit_up5 !== undefined && topic.limit_up5 !== null && topic.limit_down5 !== undefined && topic.limit_down5 !== null) {
        return `${sign}${net}(${topic.limit_up5}/${topic.limit_down5})`
      }
      return `${sign}${net}`
    },
    getNet5BadgeClass(topic) {
      if (!topic) return ''
      const net = topic.net5 !== undefined && topic.net5 !== null ? topic.net5 : topic.NET5
      if (net === undefined || net === null) return ''
      if (net > 0) return 'badge-net-positive'
      if (net < 0) return 'badge-net-negative'
      return 'badge-net-zero'
    },
    showTopicKline(topic) {
      if (topic && topic.unique_key) {
        this.selectedTopicUniqueKey = topic.unique_key
        this.selectedTopicName = topic.topic_name
        this.showTopicKlineModal = true
      }
    },
    confirmManualTrigger(code6, name) {
      this.$bvModal.msgBoxConfirm(`确定要为 [${name} (${code6})] 手动点火执行买入吗？`, {
        title: '手动点火确认',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: '确认点火',
        cancelTitle: '取消',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true
      })
      .then(value => {
        if (value) {
          this.triggerManual(code6)
        }
      })
      .catch(err => {
        console.error(err)
      })
    },
    async triggerManual(code6) {
      try {
        const res = await request_zzquant({
          url: '/v3/topic/monitor/trigger_manual',
          method: 'post',
          data: {
            code: code6
          }
        })
        if (res.data && res.data.code === 200) {
          this.$bvToast.toast(`个股 ${code6} 手动点火成功！`, {
            title: '成功',
            variant: 'success',
            solid: true
          })
          this.fetchData()
        } else {
          this.$bvToast.toast(res.data?.msg || res.data?.message || '点火失败', {
            title: '错误',
            variant: 'danger',
            solid: true
          })
        }
      } catch (e) {
        console.error(e)
        const errMsg = e.response?.data?.detail || '网络请求异常'
        this.$bvToast.toast(errMsg, {
          title: '错误',
          variant: 'danger',
          solid: true
        })
      }
    },
    shortenStatus(text) {
      if (!text || text === '-') return '-'
      let t = String(text).trim()
      t = t.replace('below_MA20', '破MA20')
      t = t.replace('no_new_high', '非新高')
      t = t.replace('非近30日新高', '非新高')
      t = t.replace('上市不足1年', '次新')
      t = t.replace('次新股', '次新')
      t = t.replace('昨日涨幅过大', '昨大涨')
      t = t.replace('未触发(已达)', '已达标')

      t = t.replace('避(偏离超买)', '避超买')
      t = t.replace('避(市值<54亿)', '避市值')
      t = t.replace('避(题材排除)', '避题材')
      t = t.replace('避(已持仓)', '避持仓')
      t = t.replace('避(今日卖出)', '避今卖')
      t = t.replace('避(低开<-8%)', '避低开')
      t = t.replace('避(昨日涨幅过大)', '避昨涨')
      t = t.replace('避雷针', '避雷')

      if (t.startsWith('买入(')) {
        t = '买' + t.slice(3, -1)
      } else if (t === '买入') {
        t = '买'
      } else if (t.startsWith('静默(')) {
        t = '静' + t.slice(3, -1)
      } else if (t === '静默') {
        t = '静'
      } else if (t.startsWith('触发(')) {
        t = '触' + t.slice(3, -1)
      } else if (t === '触发') {
        t = '触'
      } else if (t.startsWith('占位(')) {
        t = '占' + t.slice(3, -1)
      } else if (t === '占位') {
        t = '占'
      }
      return t
    },
    getRawV2Text(stock) {
      if (stock && stock.v2 && stock.v2.text) return stock.v2.text
      if (stock && stock.triggered) return `触发${stock.trigger_time ? `(${stock.trigger_time})` : ''}`
      if (stock && stock.filter_reason === '占位') return `占位${stock.trigger_time ? `(${stock.trigger_time})` : ''}`
      if (stock && stock.filter_reason) return stock.filter_reason
      if (stock && stock.is_met_buy_condition) return '未触发(已达)'
      return '未触发'
    },
    getV2BadgeText(stock) {
      return this.shortenStatus(this.getRawV2Text(stock))
    },
    getRawV3Text(stock) {
      if (stock && stock.v3 && stock.v3.text) return stock.v3.text
      return '-'
    },
    getV3BadgeText(stock) {
      return this.shortenStatus(this.getRawV3Text(stock))
    },
    isStatusMerged(stock) {
      const v2 = this.getV2BadgeText(stock)
      const v3 = this.getV3BadgeText(stock)
      if (v3 === '-') return true
      return v2 === v3
    },
    getMergedBadgeText(stock) {
      return this.getV2BadgeText(stock)
    },
    getMergedBadgeClass(stock) {
      return this.getV2BadgeClass(stock)
    },
    getStatusTooltip(stock) {
      const v2 = this.getRawV2Text(stock)
      const v3 = this.getRawV3Text(stock)
      return `V2: ${v2} | V3: ${v3}`
    },
    formatAmountMkv(amount, mkv) {
      const a = (amount || 0) / 100000000
      const m = (mkv || 0) / 100000000
      const aStr = a >= 10 ? a.toFixed(0) : a.toFixed(1)
      const mStr = m >= 10 ? m.toFixed(0) : m.toFixed(1)
      return `${aStr}/${mStr}`
    },
    getV2BadgeClass(stock) {
      const type = stock?.v2?.badge_type || stock?.v2?.status
      if (type === 'buy' || stock?.triggered) return 'badge-buy'
      if (type === 'placeholder' || stock?.filter_reason === '占位') return 'badge-placeholder'
      if (type === 'avoid' || (stock?.filter_reason && (stock.filter_reason.indexOf('避') === 0 || stock.filter_reason.indexOf('避雷') > -1))) return 'badge-avoid'
      if (type === 'muted' || (stock?.filter_reason && !stock.triggered)) return 'badge-muted-tag'
      if (type === 'met' || stock?.is_met_buy_condition) return 'badge-met-condition'
      return 'badge-untriggered'
    },
    getV3BadgeClass(stock) {
      const type = stock?.v3?.badge_type || stock?.v3?.status
      if (type === 'buy' || type === 'triggered') return 'badge-buy'
      if (type === 'silent') return 'badge-v3-silent'
      if (type === 'avoid') return 'badge-avoid'
      if (type === 'muted') return 'badge-muted-tag'
      if (type === 'met') return 'badge-met-condition'
      return 'badge-untriggered'
    },
    isCanManualTrigger(stock) {
      if (!stock) return false
      if (stock.triggered) return false
      if (stock.v2 && (stock.v2.bought || stock.v2.status === 'triggered' || stock.v2.status === 'buy')) {
        return false
      }
      return true
    },
    getScoreClass(score) {
      if (score >= 3) return 'score-high'
      if (score >= 1) return 'score-mid'
      return 'score-low'
    },
    getScoreTooltip(stock) {
      if (!stock || stock.score === undefined || stock.score === null) return ''
      const details = stock.score_details || {}

      let timing = details.timing !== undefined ? details.timing : null
      let ma5 = details.ma5 !== undefined ? details.ma5 : (details.dist_ma5 !== undefined ? details.dist_ma5 : null)
      let window = details.window !== undefined ? details.window : (details.time !== undefined ? details.time : null)
      let upRatio = details.up_ratio !== undefined ? details.up_ratio : (details.breadth !== undefined ? details.breadth : null)
      let avgPct = details.avg_pct !== undefined ? details.avg_pct : (details.theme_pct !== undefined ? details.theme_pct : null)

      // 若 details 字段缺失，从 score_str (格式如 '+2 (0+0+1+1+0)') 兜底解析 [up_ratio, avg_pct, timing, window, ma5]
      if (timing === null || ma5 === null || window === null || upRatio === null || avgPct === null) {
        if (stock.score_str && stock.score_str.indexOf('(') > -1) {
          const m = stock.score_str.match(/\(([^)]+)\)/)
          if (m && m[1]) {
            const nums = m[1].match(/[+-]?\d+/g)
            if (nums && nums.length >= 5) {
              if (upRatio === null) upRatio = parseInt(nums[0], 10) || 0
              if (avgPct === null) avgPct = parseInt(nums[1], 10) || 0
              if (timing === null) timing = parseInt(nums[2], 10) || 0
              if (window === null) window = parseInt(nums[3], 10) || 0
              if (ma5 === null) ma5 = parseInt(nums[4], 10) || 0
            }
          }
        }
      }

      timing = timing ?? 0
      ma5 = ma5 ?? 0
      window = window ?? 0
      upRatio = upRatio ?? 0
      avgPct = avgPct ?? 0

      const fmt = (label, val) => `${label}:${val > 0 ? '+' : ''}${val}`
      const dStr = [
        fmt('大盘', timing),
        fmt('均线', ma5),
        fmt('时效', window),
        fmt('广度', upRatio),
        fmt('题材', avgPct)
      ].join(' | ')

      const raw = details.raw || {}
      const parts = []
      if (raw.dist_ma5 !== undefined && raw.dist_ma5 !== null) parts.push(`乖离MA5:${raw.dist_ma5}%`)
      if (raw.trigger_time) parts.push(`时点:${raw.trigger_time}`)
      if (raw.up_count !== undefined && raw.down_count !== undefined) parts.push(`板块涨跌:${raw.up_count}/${raw.down_count}`)
      if (raw.avg_pct !== undefined && raw.avg_pct !== null) parts.push(`板块均幅:${raw.avg_pct}%`)

      const rawSuffix = parts.length > 0 ? `\n(${parts.join(', ')})` : ''
      return `确定性评分: ${stock.score > 0 ? '+' : ''}${stock.score} (${dStr})${rawSuffix}`
    }
  }
}
</script>

<style scoped>
.panorama-page {
  min-height: 100vh;
  background-color: #0f1219;
}
.bg-card-dark {
  background-color: var(--bg-card, #1a1e29);
}
.position-config-row {
  display: flex;
  align-items: flex-end;
  gap: 0.4rem;
  flex-wrap: wrap;
}
.strategy-label {
  width: 28px;
  padding-bottom: 0.3rem;
  font-weight: 700;
}
.position-input-wrap {
  width: 78px;
}
.position-input-wrap label {
  color: #8f98aa;
  display: block;
  font-size: 0.65rem;
  margin-bottom: 0;
  line-height: 1.1;
}
.position-input-wrap .form-control,
.position-input-wrap .input-group-text {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.14);
  color: #fff;
}
.position-input-wrap .form-control,
.position-input-wrap .input-group-text {
  height: 27px;
  padding-top: 0.15rem;
  padding-bottom: 0.15rem;
  font-size: 0.75rem;
}
.position-total {
  color: #9ba4b6;
  font-size: 0.75rem;
  padding-bottom: 0.3rem;
  margin-left: auto;
}
.position-total strong { color: #fff; }
.today-buy-targets strong { color: #7dd3fc; font-weight: 600; }
.crowding-card { background: var(--bg-card, #1a1e29); }
.crowding-normal { border-left: 4px solid #28a745 !important; }
.crowding-reduced { border-left: 4px solid #ffc107 !important; }
.crowding-blocked { border-left: 4px solid #dc3545 !important; }
.crowding-unknown { border-left: 4px solid #6c757d !important; }
.crowding-ratio { color: #fff; font-size: 1.75rem; font-weight: 700; line-height: 1; }
.crowding-threshold-note { color: #9ba4b6; font-size: 0.65rem; white-space: nowrap; }
.crowding-advice { color: #f3d58a; font-size: 0.85rem; font-weight: 600; }
.crowding-result { min-width: 0; }
.topic-sequence { color: #8f98aa; font-variant-numeric: tabular-nums; }
@media (max-width: 767.98px) {
  .position-input-wrap { width: calc(50% - 34px); }
  .position-total { width: 100%; margin-left: 34px; }
  .crowding-content { align-items: flex-start !important; flex-wrap: wrap; }
  .crowding-result { margin-top: 0.4rem; width: 100%; }
}
.border-light-10 {
  border-color: rgba(255, 255, 255, 0.1) !important;
}
.text-muted-gold {
  color: #bfa77b !important;
}
.bg-soft-warning {
  background-color: rgba(255, 193, 7, 0.15) !important;
  color: #ffc107 !important;
}
.table-dark {
  background-color: transparent;
}
.hover-primary:hover {
  color: var(--primary) !important;
}
.table-hover tbody tr:hover {
  background-color: rgba(255, 255, 255, 0.05);
}
.x-small {
  font-size: 0.75rem;
}
.xx-small {
  font-size: 0.65rem;
}
.compact-table td, .compact-table th {
  padding: 0.16rem 0.12rem !important;
  vertical-align: middle !important;
}
.custom-row-5 > .col-xl-20 {
  flex: 0 0 20%;
  max-width: 20%;
}
.badge-met-condition {
  background-color: rgba(139, 92, 246, 0.18) !important;
  color: #c084fc !important;
  border: 1px solid rgba(139, 92, 246, 0.4) !important;
}
.badge-avoid {
  background-color: rgba(59, 130, 246, 0.16) !important;
  color: #93c5fd !important;
  border: 1px solid rgba(59, 130, 246, 0.35) !important;
}
.badge-net-positive {
  background-color: rgba(239, 68, 68, 0.2) !important;
  color: #f87171 !important;
  border: 1px solid rgba(239, 68, 68, 0.4) !important;
}
.badge-net-zero {
  background-color: rgba(148, 163, 184, 0.15) !important;
  color: #94a3b8 !important;
  border: 1px solid rgba(148, 163, 184, 0.3) !important;
}
.badge-net-negative {
  background-color: rgba(34, 197, 94, 0.2) !important;
  color: #4ade80 !important;
  border: 1px solid rgba(34, 197, 94, 0.4) !important;
}
.bg-dark-pill {
  background-color: rgba(255, 255, 255, 0.08);
}
.border-secondary-40 {
  border-color: rgba(255, 255, 255, 0.15) !important;
}
.custom-dark-datepicker::-webkit-calendar-picker-indicator {
  filter: invert(0.8);
  cursor: pointer;
}
.micro-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.6rem !important;
  line-height: 1.15;
  padding: 1px 3px !important;
  border-radius: 2px;
  letter-spacing: -0.2px;
  white-space: nowrap;
}
.micro-prefix {
  font-weight: 700;
  margin-right: 1px;
  font-size: 0.58rem;
}
.v2-p {
  color: #ffc107;
}
.v3-p {
  color: #0dcaf0;
}
.status-badge-compact {
  font-size: 0.61rem !important;
  padding: 1px 4px !important;
  line-height: 1.15;
  border-radius: 2px;
}
.badge-buy {
  background-color: rgba(220, 53, 69, 0.24) !important;
  color: #ff6b6b !important;
  border: 1px solid rgba(220, 53, 69, 0.55) !important;
}
.badge-v3-silent {
  background-color: rgba(245, 158, 11, 0.2) !important;
  color: #fcd34d !important;
  border: 1px solid rgba(245, 158, 11, 0.45) !important;
}
.badge-placeholder {
  background-color: rgba(234, 179, 8, 0.2) !important;
  color: #fde047 !important;
  border: 1px solid rgba(234, 179, 8, 0.45) !important;
}
.badge-muted-tag {
  background-color: rgba(148, 163, 184, 0.1) !important;
  color: #94a3b8 !important;
  border: 1px solid rgba(148, 163, 184, 0.22) !important;
}
.badge-untriggered {
  background-color: rgba(255, 255, 255, 0.04) !important;
  color: #64748b !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}
.fire-btn {
  font-size: 0.68rem !important;
  line-height: 1 !important;
  cursor: pointer;
  filter: drop-shadow(0 0 2px rgba(255, 193, 7, 0.6));
  transition: transform 0.15s ease;
  vertical-align: middle;
  text-decoration: none !important;
}
.fire-btn:hover {
  transform: scale(1.25);
}
.score-pill {
  display: inline-block;
  font-family: var(--font-monospace, monospace);
  font-size: 0.58rem;
  font-weight: 700;
  line-height: 1;
  padding: 1px 3px;
  border-radius: 2px;
  cursor: help;
  white-space: nowrap;
}
.score-high {
  color: #10b981;
  background-color: rgba(16, 185, 129, 0.16);
  border: 1px solid rgba(16, 185, 129, 0.38);
}
.score-mid {
  color: #38bdf8;
  background-color: rgba(56, 189, 248, 0.14);
  border: 1px solid rgba(56, 189, 248, 0.32);
}
.score-low {
  color: #94a3b8;
  background-color: rgba(148, 163, 184, 0.1);
  border: 1px solid rgba(148, 163, 184, 0.22);
}
</style>
